import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppLayout from "@/components/AppLayout";
import { Loader2 } from "lucide-react";
// Public pages stay eager for fast first paint
import Landing from "@/pages/Landing";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import Auth from "@/pages/Auth";
import ResetPassword from "@/pages/ResetPassword";
import CustomerPortal from "@/pages/enquiry/CustomerPortal";
// Authenticated feature pages are code-split so each loads only when visited
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Playground = lazy(() => import("@/pages/Playground"));
const Simulator = lazy(() => import("@/pages/Simulator"));
const Rules = lazy(() => import("@/pages/Rules"));
const Broadcast = lazy(() => import("@/pages/Broadcast"));
const Flows = lazy(() => import("@/pages/Flows"));
const Reminders = lazy(() => import("@/pages/Reminders"));
const Settings = lazy(() => import("@/pages/Settings"));
const Team = lazy(() => import("@/pages/Team"));
const Jobs = lazy(() => import("@/pages/Jobs"));
const Tasks = lazy(() => import("@/pages/Tasks"));
const Progress = lazy(() => import("@/pages/Progress"));
const Templates = lazy(() => import("@/pages/Templates"));
const SimpleFlows = lazy(() => import("@/pages/SimpleFlows"));
const Contacts = lazy(() => import("@/pages/Contacts"));
const ChatLogs = lazy(() => import("@/pages/ChatLogs"));
const DailyFollowup = lazy(() => import("@/pages/DailyFollowup"));
const OIHR = lazy(() => import("@/pages/OIHR"));
const CRM = lazy(() => import("@/pages/CRM"));
const Customers = lazy(() => import("@/pages/Customers"));
const Vendors = lazy(() => import("@/pages/Vendors"));
const Receiving = lazy(() => import("@/pages/Receiving"));
const Trash = lazy(() => import("@/pages/Trash"));

const PageLoader = () => (
  <div className="flex items-center justify-center py-24 w-full" data-testid="page-loader">
    <Loader2 className="w-6 h-6 animate-spin text-[#22a722]" />
  </div>
);

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/login" element={<Auth mode="login" />} />
            <Route path="/signup" element={<Auth mode="signup" />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/enquiry-portal" element={<CustomerPortal />} />
            <Route
              element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/app" element={<Dashboard />} />
              <Route path="/app/playground" element={<Playground />} />
              <Route path="/app/simulator" element={<Simulator />} />
              <Route path="/app/flows" element={<Flows />} />
              <Route path="/app/rules" element={<Rules />} />
              <Route path="/app/broadcast" element={<Broadcast />} />
              <Route path="/app/reminders" element={<Reminders />} />
              <Route path="/app/contacts" element={<Contacts />} />
              <Route path="/app/team" element={<Team />} />
              <Route path="/app/jobs" element={<Jobs />} />
              <Route path="/app/oihr" element={<OIHR />} />
              <Route path="/app/crm" element={<CRM />} />
              <Route path="/app/customers" element={<Customers />} />
              <Route path="/app/vendors" element={<Vendors />} />
              <Route path="/app/receiving" element={<Receiving />} />
              <Route path="/app/tasks" element={<Tasks />} />
              <Route path="/app/followups" element={<DailyFollowup />} />
              <Route path="/app/progress" element={<Progress />} />
              <Route path="/app/quick-flows" element={<SimpleFlows />} />
              <Route path="/app/templates" element={<Templates />} />
              <Route path="/app/logs" element={<ChatLogs />} />
              <Route path="/app/settings" element={<Settings />} />
              <Route path="/app/trash" element={<Trash />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
        <Toaster position="top-right" richColors theme="dark" />
      </AuthProvider>
    </div>
  );
}

export default App;
